import {
	ArrowLeft,
	ArrowRight,
	Edit3,
	Loader2,
	Puzzle,
	Save,
} from "lucide-react";
import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	Button,
	Notification,
	useNotification,
} from "../../components/FormComponents";
import { useProposalEditor } from "../../hooks/useProposalEditor";
import StepCanvas from "./ProposalBuilder/StepCanvas";
import StepDesign from "./ProposalBuilder/StepDesign";
import StepItems from "./ProposalBuilder/StepItems";
import StepReview from "./ProposalBuilder/StepReview";
import StepSetup from "./ProposalBuilder/StepSetup";
import SuccessScreen from "./ProposalBuilder/SuccessScreen";

export default function ProposalNew() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const editId = searchParams.get("edit");
	const { notification, showNotification } = useNotification();

	const {
		companies,
		clients,
		plans,
		modules,
		templatesByCategory,
		selectedTemplates,
		loading,
		saving,
		errors,
		setErrors,
		step,
		setStep,
		createdProposal,
		formData,
		setFormData,
		isDirty,
		visualBuilder,
		handleCategoryTemplateSelect,
		handleSubmit,
		handleNext,
		handleBack,
		isEditing,
		hasCodeTemplate,
	} = useProposalEditor(editId, showNotification);

	const {
		sections,
		selectedItems,
		addSection,
		removeSection,
		moveSection,
		duplicateSection,
		updateSection,
		addItem,
		removeItem,
		updateItem,
		totalValue,
	} = visualBuilder;

	// Warn on unsaved changes
	useEffect(() => {
		const handler = (e) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);

	if (loading)
		return (
			<div className="flex items-center justify-center py-12 text-slate-300">
				<Loader2 className="w-6 h-6 animate-spin mr-2" />
				Carregando dados do editor...
			</div>
		);

	if (createdProposal)
		return (
			<SuccessScreen
				proposal={createdProposal}
				onNavigate={navigate}
				showNotification={showNotification}
				notification={notification}
			/>
		);

	const allSteps = [
		{ num: 1, label: "Cliente e Empresa" },
		{ num: 2, label: "Conteúdo" },
		{ num: 3, label: "Itens" },
		{ num: 4, label: "Design" },
		{ num: 5, label: "Revisar" },
	];

	const steps = hasCodeTemplate
		? [
				{ num: 1, label: "Cliente e Empresa" },
				{ num: 5, label: "Revisar" },
			]
		: allSteps;

	return (
		<div className="space-y-6">
			<Notification notification={notification} />

			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition"
						aria-label="Voltar"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div className="flex items-center gap-2">
						{isEditing ? <Edit3 className="w-5 h-5 text-amber-400" /> : null}
						<h1 className="text-2xl font-bold text-white">
							{isEditing ? "Editar Proposta" : "Nova Proposta"}
						</h1>
					</div>
				</div>
				<div className="flex items-center gap-3">
					{step > 1 && (
						<Button variant="secondary" onClick={handleBack} type="button">
							Voltar
						</Button>
					)}
					{hasCodeTemplate && step < 5 ? (
						<Button
							onClick={handleSubmit}
							disabled={saving}
							type="button"
							className="bg-green-600 hover:bg-green-700 text-white border-none flex items-center"
						>
							{saving ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Save className="w-4 h-4 mr-2" />
							)}
							{isEditing ? "Salvar" : "Finalizar"}
						</Button>
					) : null}
					{step < 5 ? (
						<Button onClick={handleNext} type="button">
							Próximo
						</Button>
					) : (
						<Button onClick={handleSubmit} disabled={saving} type="button">
							{saving ? (
								<Loader2 className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<Save className="w-4 h-4 mr-2" />
							)}
							{isEditing ? "Salvar" : "Finalizar"}
						</Button>
					)}
				</div>
			</div>

			<div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
				{steps.map((s, idx) => (
					<React.Fragment key={s.num}>
						<button
							type="button"
							onClick={() => s.num < step && setStep(s.num)}
							className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap border transition ${
								s.num === step
									? "bg-cyan-600/20 text-cyan-300 border-cyan-500/30"
									: s.num < step
										? "bg-green-600/10 text-green-400 border-green-500/20"
										: "bg-slate-800/30 text-slate-500 border-slate-700/50"
							}`}
						>
							<span
								className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${
									s.num === step
										? "bg-cyan-600 text-white"
										: s.num < step
											? "bg-green-600 text-white"
											: "bg-slate-700"
								}`}
							>
								{s.num < step ? "✓" : s.num}
							</span>
							{s.label}
						</button>
						{idx < steps.length - 1 && (
							<div
								className={`flex-1 min-w-[20px] h-px ${s.num < step ? "bg-green-500/30" : "bg-slate-700"}`}
							/>
						)}
					</React.Fragment>
				))}
			</div>

			{step === 2 ? (
				<div className="w-full">
					<StepCanvas
						formData={formData}
						setFormData={setFormData}
						sections={sections}
						addSection={addSection}
						updateSection={updateSection}
						removeSection={removeSection}
						moveSection={moveSection}
						duplicateSection={duplicateSection}
						companies={companies}
						selectedItems={selectedItems}
						showNotification={showNotification}
					/>
				</div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<main className="lg:col-span-2">
						{step === 1 && (
							<StepSetup
								formData={formData}
								setFormData={setFormData}
								clients={clients}
								companies={companies}
								templatesByCategory={templatesByCategory}
								selectedTemplates={selectedTemplates}
								handleCategoryTemplateSelect={handleCategoryTemplateSelect}
								errors={errors}
								setErrors={setErrors}
								editId={editId}
							/>
						)}
						{step === 3 && (
							<StepItems
								selectedItems={selectedItems}
								addItem={addItem}
								removeItem={removeItem}
								updateItem={updateItem}
								plans={plans}
								modules={modules}
								errors={errors}
								totalValue={totalValue}
							/>
						)}
						{step === 4 && (
							<StepDesign formData={formData} setFormData={setFormData} />
						)}
						{step === 5 && (
							<StepReview
								formData={formData}
								setFormData={setFormData}
								sections={sections}
								selectedItems={selectedItems}
								companies={companies}
								clients={clients}
								totalValue={totalValue}
								errors={errors}
							/>
						)}
					</main>

					{/* Sidebar Summary */}
					<aside className="space-y-6">
						<div className="bg-slate-900/50 backdrop-blur border border-white/5 rounded-xl p-6 sticky top-6">
							<h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
								Resumo da Proposta
							</h3>
							<div className="space-y-3">
								<div className="flex justify-between items-center text-sm">
									<span className="text-slate-500">Itens</span>
									<span className="text-white font-medium">
										{selectedItems.length}
									</span>
								</div>
								<div className="flex justify-between items-center text-sm">
									<span className="text-slate-500">Seções</span>
									<span className="text-white font-medium">
										{sections.length}
									</span>
								</div>
								<div className="pt-3 border-t border-slate-800 flex justify-between items-center">
									<span className="text-sm font-bold text-white uppercase">
										Valor Total
									</span>
									<span className="text-lg font-bold text-green-400">
										R${" "}
										{totalValue.toLocaleString("pt-BR", {
											minimumFractionDigits: 2,
										})}
									</span>
								</div>
							</div>

							<div className="mt-6 flex flex-col gap-2">
								{step > 1 && (
									<Button
										variant="secondary"
										className="w-full"
										onClick={handleBack}
										type="button"
									>
										<ArrowLeft className="w-4 h-4 mr-2" /> Voltar
									</Button>
								)}
								{step < 5 ? (
									<Button className="w-full" onClick={handleNext} type="button">
										Próximo <ArrowRight className="w-4 h-4 ml-2" />
									</Button>
								) : (
									<Button
										className="w-full"
										onClick={handleSubmit}
										loading={saving}
										type="button"
									>
										<Save className="w-4 h-4 mr-2" />{" "}
										{isEditing ? "Salvar Alterações" : "Criar Proposta"}
									</Button>
								)}
							</div>
						</div>

						<div className="bg-cyan-600/10 border border-cyan-500/20 rounded-xl p-4">
							<div className="flex items-center gap-2 mb-2 text-cyan-400">
								<Puzzle className="w-4 h-4" />
								<span className="text-xs font-bold uppercase tracking-wider">
									Templates
								</span>
							</div>
							<p className="text-[10px] text-cyan-300/70 leading-relaxed">
								Use templates pré-definidos para acelerar a criação de seções ou
								itens. Você pode combinar múltiplos templates ou usar um
								completo.
							</p>
						</div>
					</aside>
				</div>
			)}
		</div>
	);
}
